/**
 * Pagina configurazione e diagnostica registratore telematico.
 *
 * Consente di:
 * - Visualizzare lo stato live del dispositivo (89F/88F)
 * - Scansionare la rete per trovare il registratore
 * - Configurare IP, porta e parametri (editabili, con salvataggio)
 * - Eseguire comandi di test (cassetto, scontrino non fiscale)
 * - Eseguire rapporti X (lettura) e Z (azzeramento)
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Cpu,
  FileText,
  Inbox,
  Monitor,
  Printer,
  RefreshCw,
  Save,
  Search,
  Settings,
  Wifi,
  WifiOff,
  XCircle,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RTStatus {
  logotipo: string;
  modello: string;
  costruttore: string;
  matricola: string;
  stato_fiscale: string;
  stato_fiscale_label: string;
  stato_scontrino: string;
  stato_scontrino_label: string;
  tipo_scontrino: string;
  numero_scontrino_fiscale: number;
  numero_scontrino_gestionale: number;
  occupazione_dgfe: number;
  occupazione_mf: number;
  stato_stampante: string;
  stato_stampante_label: string;
  stato_carta: string;
  stato_carta_label: string;
  stato_mmc2: string;
  stato_mmc2_label: string;
}

interface RTStatusHouse {
  matricola_rt: string;
  versione_db: string;
  numero_dgfe: string;
  tipo_registratore: number;
  tipo_registratore_label: string;
  numero_azzeramento: number;
  numero_azzeramenti_pending: number;
}

interface RTConfig {
  host: string;
  port: number;
  timeout_s: number;
  timeout_lettura_s: number;
  tx_footer_mode: number;
  echo: boolean;
  reconnect_delay_s: number;
  max_reconnect: number;
}

interface RTFullStatus {
  status: RTStatus;
  status_house: RTStatusHouse;
  configurazione: RTConfig;
}

interface RTCommandResult {
  successo: boolean;
  comando: string;
  messaggio: string;
  errore: string | null;
}

interface RTScanDevice {
  host: string;
  port: number;
  modello: string | null;
  matricola: string | null;
}

interface RTScanResponse {
  dispositivi: RTScanDevice[];
  scansionati: number;
  durata_ms: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConfigurazioneRT: React.FC = () => {
  const [fullStatus, setFullStatus] = useState<RTFullStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [commandResult, setCommandResult] = useState<RTCommandResult | null>(null);
  const [executingCommand, setExecutingCommand] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ref per scroll al messaggio
  const topRef = useRef<HTMLDivElement>(null);

  // Display message input
  const [displayLinea1, setDisplayLinea1] = useState('FLORALTECH ERP');
  const [displayLinea2, setDisplayLinea2] = useState('');

  // Config form state
  const [cfgHost, setCfgHost] = useState('');
  const [cfgPort, setCfgPort] = useState('1723');
  const [cfgTimeout, setCfgTimeout] = useState('30');
  const [cfgTimeoutLettura, setCfgTimeoutLettura] = useState('60');
  const [cfgTxFooter, setCfgTxFooter] = useState('3');
  const [cfgEcho, setCfgEcho] = useState(false);
  const [cfgReconnectDelay, setCfgReconnectDelay] = useState('5');
  const [cfgMaxReconnect, setCfgMaxReconnect] = useState('3');
  const [cfgDirty, setCfgDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Scan state
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState<RTScanDevice[] | null>(null);
  const [scanDuration, setScanDuration] = useState(0);

  // Scroll to top on command result
  useEffect(() => {
    if (commandResult) {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [commandResult]);

  // Populate form from loaded config
  const populateForm = useCallback((cfg: RTConfig) => {
    setCfgHost(cfg.host);
    setCfgPort(String(cfg.port));
    setCfgTimeout(String(cfg.timeout_s));
    setCfgTimeoutLettura(String(cfg.timeout_lettura_s));
    setCfgTxFooter(String(cfg.tx_footer_mode));
    setCfgEcho(cfg.echo);
    setCfgReconnectDelay(String(cfg.reconnect_delay_s));
    setCfgMaxReconnect(String(cfg.max_reconnect));
    setCfgDirty(false);
  }, []);

  // Load status
  const caricaStato = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/cassa/rt/stato', { headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `Errore ${res.status}` }));
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      const data: RTFullStatus = await res.json();
      setFullStatus(data);
      populateForm(data.configurazione);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      // Load config even if status fails
      try {
        const res = await fetch('/api/v1/cassa/rt/config', { headers: authHeaders() });
        if (res.ok) {
          const cfg: RTConfig = await res.json();
          populateForm(cfg);
        }
      } catch { /* ignore */ }
    } finally {
      setLoading(false);
    }
  }, [populateForm]);

  useEffect(() => {
    void caricaStato();
  }, [caricaStato]);

  // Mark dirty on any form change
  const updateField = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (value: T) => { setter(value); setCfgDirty(true); };

  // Save config
  const salvaConfig = async () => {
    setSaving(true);
    setCommandResult(null);
    try {
      const res = await fetch('/api/v1/cassa/rt/config', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          host: cfgHost,
          port: Number(cfgPort),
          timeout_s: Number(cfgTimeout),
          timeout_lettura_s: Number(cfgTimeoutLettura),
          tx_footer_mode: Number(cfgTxFooter),
          echo: cfgEcho,
          reconnect_delay_s: Number(cfgReconnectDelay),
          max_reconnect: Number(cfgMaxReconnect),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `Errore ${res.status}` }));
        throw new Error(err.detail ?? 'Errore salvataggio');
      }
      const cfg: RTConfig = await res.json();
      populateForm(cfg);
      setCommandResult({
        successo: true,
        comando: 'salva-config',
        messaggio: 'Configurazione salvata',
        errore: null,
      });
    } catch (err) {
      setCommandResult({
        successo: false,
        comando: 'salva-config',
        messaggio: 'Errore salvataggio configurazione',
        errore: err instanceof Error ? err.message : 'Errore sconosciuto',
      });
    } finally {
      setSaving(false);
    }
  };

  // Scan network
  const scanRete = async () => {
    setScanning(true);
    setScanResults(null);
    setCommandResult(null);
    try {
      const res = await fetch('/api/v1/cassa/rt/scan', {
        method: 'POST',
        headers: authHeaders(),
      });
      const data: RTScanResponse = await res.json();
      setScanResults(data.dispositivi);
      setScanDuration(data.durata_ms);
      if (data.dispositivi.length === 0) {
        setCommandResult({
          successo: false,
          comando: 'scan',
          messaggio: `Nessun registratore trovato (${data.scansionati} IP scansionati in ${(data.durata_ms / 1000).toFixed(1)}s)`,
          errore: null,
        });
      }
    } catch {
      setCommandResult({
        successo: false,
        comando: 'scan',
        messaggio: 'Errore scansione rete',
        errore: 'Impossibile comunicare con il backend',
      });
    } finally {
      setScanning(false);
    }
  };

  // Use scan result
  const usaDispositivo = (device: RTScanDevice) => {
    setCfgHost(device.host);
    setCfgPort(String(device.port));
    setCfgDirty(true);
    setScanResults(null);
    setCommandResult({
      successo: true,
      comando: 'scan',
      messaggio: `Selezionato ${device.modello ?? 'dispositivo'} su ${device.host}:${device.port} — salva per applicare`,
      errore: null,
    });
  };

  // Execute command
  const eseguiComando = async (endpoint: string, label: string) => {
    setExecutingCommand(label);
    setCommandResult(null);
    try {
      const res = await fetch(`/api/v1/cassa/rt/${endpoint}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data: RTCommandResult = await res.json();
      setCommandResult(data);
    } catch {
      setCommandResult({
        successo: false,
        comando: label,
        messaggio: 'Errore di rete',
        errore: 'Impossibile comunicare con il backend',
      });
    } finally {
      setExecutingCommand(null);
    }
  };

  const st = fullStatus?.status;
  const sh = fullStatus?.status_house;

  return (
    <div className="space-y-6 p-6">
      <div ref={topRef} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
            Registratore di Cassa
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Diagnostica e configurazione dispositivo RT
          </p>
        </div>
        <Button onClick={caricaStato} disabled={loading} variant="outline">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna stato
        </Button>
      </div>

      {/* Errore globale */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-3 pt-6">
            <WifiOff className="h-5 w-5 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Dispositivo non raggiungibile</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risultato ultimo comando */}
      {commandResult && (
        <Card className={commandResult.successo ? 'border-green-500' : 'border-destructive'}>
          <CardContent className="flex items-center gap-3 pt-6">
            {commandResult.successo ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-destructive" />
            )}
            <div>
              <p className={`font-medium ${commandResult.successo ? 'text-green-700 dark:text-green-400' : 'text-destructive'}`}>
                {commandResult.messaggio}
              </p>
              {commandResult.errore && (
                <p className="text-sm text-muted-foreground">{commandResult.errore}</p>
              )}
            </div>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setCommandResult(null)}>
              <XCircle className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stato dispositivo */}
      {st && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground">
                <Cpu className="h-4 w-4" /> Dispositivo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{st.modello}</p>
              <p className="text-sm text-muted-foreground">Matricola: {st.matricola}</p>
              <p className="text-sm text-muted-foreground">
                {st.costruttore === '3C' ? '3i Retail Solutions' : st.costruttore}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" /> Stato Fiscale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={st.stato_fiscale === 'F' ? 'default' : 'secondary'}>
                {st.stato_fiscale_label}
              </Badge>
              <p className="mt-2 text-sm text-muted-foreground">
                Fiscali: <span className="font-mono font-medium">{st.numero_scontrino_fiscale}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Gestionali: <span className="font-mono font-medium">{st.numero_scontrino_gestionale}</span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground">
                <Printer className="h-4 w-4" /> Stampante
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm">Stampante:</span>
                <Badge variant={st.stato_stampante === 'O' ? 'outline' : 'destructive'}>{st.stato_stampante_label}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Carta:</span>
                <Badge variant={st.stato_carta === 'O' ? 'outline' : st.stato_carta === 'I' ? 'secondary' : 'destructive'}>{st.stato_carta_label}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Scontrino:</span>
                <Badge variant={st.stato_scontrino === 'C' ? 'outline' : 'secondary'}>{st.stato_scontrino_label}</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground">
                <Settings className="h-4 w-4" /> Firmware
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sh && (
                <>
                  <p className="text-sm"><span className="text-muted-foreground">Versione:</span> <span className="font-mono font-medium">{sh.versione_db}</span></p>
                  <p className="text-sm"><span className="text-muted-foreground">Matricola RT:</span> <span className="font-mono font-medium">{sh.matricola_rt}</span></p>
                  <p className="text-sm"><span className="text-muted-foreground">Tipo:</span> <span className="font-medium">{sh.tipo_registratore_label}</span></p>
                  <p className="text-sm"><span className="text-muted-foreground">Azzeramenti:</span> <span className="font-mono font-medium">{sh.numero_azzeramento}</span></p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Memoria */}
      {st && (st.occupazione_dgfe >= 0 || st.occupazione_mf >= 0) && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Activity className="h-5 w-5" /> Memoria</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {st.occupazione_dgfe >= 0 && (
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm"><span>DGFE</span><span className="font-mono">{st.occupazione_dgfe}%</span></div>
                  <div className="h-2 rounded-full bg-muted">
                    <div className={`h-full rounded-full ${st.occupazione_dgfe > 80 ? 'bg-destructive' : st.occupazione_dgfe > 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${st.occupazione_dgfe}%` }} />
                  </div>
                </div>
              )}
              {st.occupazione_mf >= 0 && (
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm"><span>Memoria Fiscale</span><span className="font-mono">{st.occupazione_mf}%</span></div>
                  <div className="h-2 rounded-full bg-muted">
                    <div className={`h-full rounded-full ${st.occupazione_mf > 80 ? 'bg-destructive' : st.occupazione_mf > 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${st.occupazione_mf}%` }} />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configurazione — editabile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2"><Settings className="h-5 w-5" /> Configurazione</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={scanRete} disabled={scanning}>
                <Search className={`mr-2 h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
                {scanning ? 'Scansione...' : 'Cerca in rete'}
              </Button>
              {cfgDirty && (
                <Button size="sm" onClick={salvaConfig} disabled={saving}>
                  <Save className={`mr-2 h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
                  Salva
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Scan results */}
          {scanResults && scanResults.length > 0 && (
            <div className="mb-6 rounded-lg border border-green-500/30 bg-green-50 p-4 dark:bg-green-950/20">
              <p className="mb-2 text-sm font-medium text-green-700 dark:text-green-400">
                {scanResults.length} dispositivo/i trovato/i ({(scanDuration / 1000).toFixed(1)}s)
              </p>
              <div className="space-y-2">
                {scanResults.map((d, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border bg-background p-3">
                    <div>
                      <span className="font-mono font-medium">{d.host}:{d.port}</span>
                      {d.modello && <span className="ml-3 text-sm text-muted-foreground">{d.modello}</span>}
                      {d.matricola && <span className="ml-2 text-sm text-muted-foreground">({d.matricola})</span>}
                    </div>
                    <Button size="sm" onClick={() => usaDispositivo(d)}>Usa questo</Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
            {/* Connessione */}
            <div>
              <label className="mb-1 block text-sm font-medium">Indirizzo IP</label>
              <Input
                value={cfgHost}
                onChange={(e) => { setCfgHost(e.target.value); setCfgDirty(true); }}
                placeholder="192.168.178.32"
              />
              <p className="mt-1 text-xs text-muted-foreground">IP del registratore sulla rete locale</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Porta TCP</label>
              <Input
                type="number"
                value={cfgPort}
                onChange={(e) => { setCfgPort(e.target.value); setCfgDirty(true); }}
                placeholder="1723"
              />
              <p className="mt-1 text-xs text-muted-foreground">Default: 1723</p>
            </div>

            {/* Timeout */}
            <div>
              <label className="mb-1 block text-sm font-medium">Timeout comandi (s)</label>
              <Input
                type="number"
                step="0.5"
                value={cfgTimeout}
                onChange={(e) => { setCfgTimeout(e.target.value); setCfgDirty(true); }}
              />
              <p className="mt-1 text-xs text-muted-foreground">Default: 30s</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Timeout letture (s)</label>
              <Input
                type="number"
                step="0.5"
                value={cfgTimeoutLettura}
                onChange={(e) => { setCfgTimeoutLettura(e.target.value); setCfgDirty(true); }}
              />
              <p className="mt-1 text-xs text-muted-foreground">Default: 60s — per DGFE e memoria fiscale</p>
            </div>

            {/* TX_FOOTER */}
            <div>
              <label className="mb-1 block text-sm font-medium">Formato TX_FOOTER</label>
              <Select value={cfgTxFooter} onValueChange={(v) => { setCfgTxFooter(v); setCfgDirty(true); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 — Nessuno</SelectItem>
                  <SelectItem value="1">1 — MF</SelectItem>
                  <SelectItem value="2">2 — RT</SelectItem>
                  <SelectItem value="3">3 — JSON (consigliato)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">Default: 3 (JSON) — risposta dopo chiusura scontrino</p>
            </div>

            {/* Riconnessione */}
            <div>
              <label className="mb-1 block text-sm font-medium">Tentativi riconnessione</label>
              <Input
                type="number"
                value={cfgMaxReconnect}
                onChange={(e) => { setCfgMaxReconnect(e.target.value); setCfgDirty(true); }}
              />
              <p className="mt-1 text-xs text-muted-foreground">Default: 3</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Delay riconnessione (s)</label>
              <Input
                type="number"
                step="0.5"
                value={cfgReconnectDelay}
                onChange={(e) => { setCfgReconnectDelay(e.target.value); setCfgDirty(true); }}
              />
              <p className="mt-1 text-xs text-muted-foreground">Default: 5s</p>
            </div>

            {/* Echo */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={cfgEcho}
                onChange={(e) => { setCfgEcho(e.target.checked); setCfgDirty(true); }}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <div>
                <label className="text-sm font-medium">Echo</label>
                <p className="text-xs text-muted-foreground">Il registratore ripete i comandi ricevuti</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comandi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Zap className="h-5 w-5" /> Comandi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Button variant="outline" className="justify-start" onClick={() => eseguiComando('apri-cassetto', 'Apri cassetto')} disabled={!!executingCommand}>
              <Inbox className={`mr-2 h-4 w-4 ${executingCommand === 'Apri cassetto' ? 'animate-bounce' : ''}`} />
              Apri cassetto
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => eseguiComando('scontrino-test', 'Scontrino test')} disabled={!!executingCommand}>
              <FileText className={`mr-2 h-4 w-4 ${executingCommand === 'Scontrino test' ? 'animate-spin' : ''}`} />
              Scontrino di test
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => eseguiComando('rapporto-x', 'Rapporto X')} disabled={!!executingCommand}>
              <FileText className={`mr-2 h-4 w-4 ${executingCommand === 'Rapporto X' ? 'animate-spin' : ''}`} />
              Rapporto X (lettura)
            </Button>
            <Button variant="secondary" className="justify-start border border-destructive/30" disabled={!!executingCommand}
              onClick={() => {
                if (window.confirm('ATTENZIONE: Il rapporto Z azzera i totali giornalieri.\n\nEseguire solo a fine giornata.\n\nProcedere?')) {
                  void eseguiComando('rapporto-z', 'Rapporto Z');
                }
              }}>
              <AlertTriangle className={`mr-2 h-4 w-4 text-destructive ${executingCommand === 'Rapporto Z' ? 'animate-spin' : ''}`} />
              Rapporto Z (chiusura)
            </Button>
          </div>

          {/* Display message */}
          <div className="mt-4 border-t pt-4">
            <p className="mb-2 text-sm font-medium">Messaggio display</p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Riga 1 (max 16 char)</label>
                <Input
                  value={displayLinea1}
                  onChange={(e) => setDisplayLinea1(e.target.value.slice(0, 16))}
                  placeholder="Riga 1"
                  className="w-48 font-mono"
                  maxLength={16}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Riga 2 (max 16 char)</label>
                <Input
                  value={displayLinea2}
                  onChange={(e) => setDisplayLinea2(e.target.value.slice(0, 16))}
                  placeholder="Riga 2 (opzionale)"
                  className="w-48 font-mono"
                  maxLength={16}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  const params = new URLSearchParams({ linea1: displayLinea1 });
                  if (displayLinea2) params.set('linea2', displayLinea2);
                  void eseguiComando(`display?${params.toString()}`, 'Display');
                }}
                disabled={!!executingCommand || !displayLinea1}
              >
                <Monitor className={`mr-2 h-4 w-4 ${executingCommand === 'Display' ? 'animate-pulse' : ''}`} />
                Invia al display
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigurazioneRT;
